import { getAuthUser } from 'utils';

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu } from 'antd';

import ModalMessage from 'components/shared/ModalMessage';
import VoteResultsModal from 'components/shared/VoteResultsModal';
import { POST_TYPE } from 'constants/global';
import { Meeting, Poll, Topic } from 'constants/types';
import { useStopPollMutation } from 'services/pollManagement';
import { useDeleteTopicMutation } from 'services/topicManagement';

import EditMeetingModal from '../../ForumDashboard/components/AdminDashboard/components/Edit/EditMeetingModal';
import EditTopicModal from '../../ForumDashboard/components/AdminDashboard/components/Edit/EditTopicModal';
import { StyledDropdown } from './styles';

interface DropdownMenuProps {
  post: Topic | Meeting | Poll;
  type: POST_TYPE;
  refetchPinned: () => void;
  refetchUnpinned: () => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  post,
  type,
  refetchPinned,
  refetchUnpinned,
}) => {
  const { t } = useTranslation('forum');
  const [isVoteModalOpen, setIsVoteModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEndPollModalOpen, setIsEndPollModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [deletePost] = useDeleteTopicMutation();
  const [stopPoll] = useStopPollMutation();

  const authUser = getAuthUser();
  const isAuthor = authUser?.email === post?.author?.email;
  const isPoll = type === POST_TYPE.POLL;
  const isTopic = type === POST_TYPE.TOPIC;
  const isMeet = type === POST_TYPE.MEET;
  const isStopped = !(post as Poll).stopped;
  const isAnonymous = !(post as Poll).isAnonymous;

  const onDeletePost = useCallback(async () => {
    setIsDeleteModalOpen(false);
    await deletePost({ id: post._id, type });
    refetchPinned();
    refetchUnpinned();
  }, [post._id, deletePost, refetchPinned, refetchUnpinned]);

  const onEndPoll = useCallback(async () => {
    setIsEndPollModalOpen(false);
    try {
      await stopPoll({ id: post._id });
      refetchPinned();
      refetchUnpinned();
    } catch (error) {
      console.error('Error stopping poll:', error);
    }
  }, [post._id, stopPoll, refetchPinned, refetchUnpinned]);

  const onToggleEditModal = useCallback(() => {
    setIsEditModalOpen((prev) => !prev);
  }, []);

  const onToggleVoteModal = () => {
    setIsVoteModalOpen((prev) => !prev);
  };

  const menu = (
    <Menu>
      {(isTopic || isMeet) && (
        <Menu.Item key="edit" onClick={onToggleEditModal}>
          {t('edit')}
        </Menu.Item>
      )}
      {isStopped && isPoll && isAuthor && (
        <Menu.Item key="stop_poll" onClick={() => setIsEndPollModalOpen(true)}>
          {t('stop_poll')}
        </Menu.Item>
      )}
      {isPoll && isAnonymous && (
        <Menu.Item key="vote_results" onClick={onToggleVoteModal}>
          {t('vote_results')}
        </Menu.Item>
      )}
      {isAuthor && (
        <Menu.Item key="delete" onClick={() => setIsDeleteModalOpen(true)}>
          {t('delete')}
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <>
      {isDeleteModalOpen && (
        <ModalMessage
          messageType="warining"
          title={
            isPoll
              ? t('delete_poll_title')
              : isMeet
                ? t('delete_meeting_title')
                : t('delete_topic_title')
          }
          subTitle={
            isPoll
              ? t('delete_poll_sub_title')
              : isMeet
                ? t('delete_meeting_sub_title')
                : t('delete_topic_sub_title')
          }
          btnText={t('ok')}
          handleBtn={onDeletePost}
          onCancelClick={() => setIsDeleteModalOpen(false)}
        />
      )}

      {isEndPollModalOpen && (
        <ModalMessage
          messageType="warining"
          title={t('stop_poll_title')}
          subTitle={t('stop_poll_sub_title')}
          btnText={t('ok')}
          handleBtn={onEndPoll}
          onCancelClick={() => setIsEndPollModalOpen(false)}
        />
      )}

      {isVoteModalOpen && type === POST_TYPE.POLL && (
        <VoteResultsModal
          isVisible={isVoteModalOpen}
          onClose={() => setIsVoteModalOpen(false)}
          pollId={post._id}
        />
      )}

      {isEditModalOpen && type === POST_TYPE.TOPIC && (
        <EditTopicModal
          topic={post as Topic}
          isEditModalOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          refetchPinned={refetchPinned}
          refetchUnpinned={refetchUnpinned}
        />
      )}

      {isEditModalOpen && type === POST_TYPE.MEET && (
        <EditMeetingModal
          meeting={post as Meeting}
          isEditModalOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          refetchPinned={refetchPinned}
          refetchUnpinned={refetchUnpinned}
        />
      )}

      {isAuthor && (
        <StyledDropdown
          overlay={menu}
          trigger={['click']}
          placement="bottomRight"
        >
          <span>...</span>
        </StyledDropdown>
      )}
    </>
  );
};

export default DropdownMenu;
